import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/article";

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  const go = (next: number) => setIndex((next + items.length) % items.length);
  const current = items[index];

  return (
    <section className="relative h-[520px] w-full overflow-hidden md:h-[660px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title} 黑丝搭配焦点内容`}
            title={`${item.title} - 黑丝时尚搭配`}
            className="h-full w-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/30" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1200px] flex-col justify-end px-4 pb-16 md:px-8 md:pb-20">
        <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full bg-primary-500 px-4 py-1.5 text-xs font-semibold text-background-50">
          <i className="ri-fire-line" />
          编辑精选 · 焦点内容
        </span>
        <h1 className="max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-5xl">
          {current.title}
        </h1>
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/90 md:text-base">
          {current.excerpt}
        </p>
        <div className="mt-7 flex flex-wrap items-center gap-4">
          <Link
            to={`/detail/${current.id}`}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-500 hover:text-background-50"
          >
            阅读全文
            <i className="ri-arrow-right-line" />
          </Link>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/50 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
          >
            浏览分类
          </Link>
        </div>

        <div className="mt-10 flex items-center gap-4">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-background-50" : "w-3 bg-background-50/45 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(index - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(index + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-50/40 text-background-50 transition-colors hover:bg-background-50/15 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}